import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Row, Col, Card, Divider, Switch, Tag, Typography, Select, Alert, Space, Tabs } from 'antd';
import { 
  CheckOutlined, 
  GlobalOutlined, 
  BookOutlined, 
  InfoCircleOutlined,
  SettingOutlined,
  FileTextOutlined,
  ThunderboltOutlined,
  SafetyCertificateOutlined
} from '@ant-design/icons';
import { kbApiClient as axios, KB_API_BASE } from '../api';

const { TextArea } = Input;

// Convert a display name to a safe snake_case templateId
const toTemplateId = (name = '') =>
  name
    .normalize('NFD')                        // decompose accents + strip emoji combining chars
    .replace(/[\u0300-\u036f]/g, '')         // remove combining diacritics
    .replace(/[^\w\s-]/g, '')               // strip emojis and punctuation
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_')               // spaces/hyphens → underscore
    .replace(/[^a-z0-9_]/g, '')            // final sanitise
    .replace(/^_+|_+$/g, '');             // trim leading/trailing underscores

const TemplateForm = ({ initialValues, functions, onSubmit, onCancel }) => {
  const [form] = Form.useForm();
  const [selectedFunctions, setSelectedFunctions] = useState(
    initialValues?.enabledFunctions || []
  );
  const [previewId, setPreviewId] = useState(
    initialValues?.templateId || ''
  );
  const [libraryFiles, setLibraryFiles] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(false);

  useEffect(() => {
    const loadLibrary = async () => {
      setLibraryLoading(true);
      try {
        const res = await axios.get(`${KB_API_BASE}/library`);
        if (res.data.success) {
          setLibraryFiles(res.data.files || []);
        }
      } catch (err) {
        console.error('Failed to load knowledge library', err);
      } finally {
        setLibraryLoading(false);
      }
    };
    loadLibrary();
  }, []);

  const isEdit = !!initialValues?.templateId;

  const handleNameChange = (e) => {
    if (isEdit) return;          // never change an existing agent's ID
    setPreviewId(toTemplateId(e.target.value));
  };

  const handleSubmit = async (values) => {
    const payload = {
      ...values,
      templateId: isEdit ? initialValues.templateId : toTemplateId(values.agentName),
      enabledFunctions: selectedFunctions,
    };
    onSubmit(payload);
  };

  const toggleFunction = (funcName) => {
    if (selectedFunctions.includes(funcName)) {
      setSelectedFunctions(selectedFunctions.filter(f => f !== funcName));
    } else {
      setSelectedFunctions([...selectedFunctions, funcName]);
    }
  };

  // Group functions by category
  const groupedFunctions = functions.reduce((acc, func) => {
    const category = func.category || 'Other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(func);
    return acc;
  }, {});

  const tabItems = [
    {
      key: 'general',
      label: <span><SettingOutlined /> General Configuration</span>,
      children: (
        <div style={{ padding: '16px 0' }}>
          <Form.Item
            label="Agent Name"
            name="agentName"
            rules={[{ required: true, message: 'Please enter an agent name' }]}
            extra={
              previewId
                ? <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    ID: <Tag style={{ marginLeft: 2, fontFamily: 'monospace', fontSize: 11 }}>{previewId}</Tag>
                    {!isEdit && <span style={{ color: '#aaa' }}>— auto-generated, used internally</span>}
                  </Typography.Text>
                : null
            }
          >
            <Input
              placeholder="e.g., Sales Advisor, Support Bot, Booking Assistant"
              onChange={handleNameChange}
              disabled={isEdit}
            />
          </Form.Item>

          <Form.Item
            label="Role"
            name="role"
            extra="One line — what does this agent handle? Helps the orchestrator route correctly."
            rules={[{ required: true, message: 'Please describe what this agent does' }]}
          >
            <Input placeholder="e.g., Handles product questions and recommends the right plan" />
          </Form.Item>

          <Form.Item
            label="System Prompt"
            name="systemPrompt"
            rules={[{ required: true, message: 'Please enter a system prompt' }]}
            extra="Define the agent's personality, role, and behavior"
          >
            <TextArea 
              rows={8}
              placeholder="You are a helpful assistant that..."
            />
          </Form.Item>

          <Form.Item
            label="Guard Rail Prompt"
            name="guardRailPrompt"
            extra="Boundaries the agent must never cross. Every reply is checked against this; if it violates, the reply is replaced with a safe one before sending."
          >
            <TextArea 
              rows={4}
              placeholder="e.g. Never share personal data. Never give medical or legal advice. Never use offensive language. Stay on topic for travel insurance only."
            />
          </Form.Item>
        </div>
      )
    },
    {
      key: 'knowledge',
      label: <span><FileTextOutlined /> Knowledge Base</span>,
      children: (
        <div style={{ padding: '16px 0' }}>
          {libraryFiles.length === 0 && !libraryLoading ? (
            <Alert
              message="No Knowledge Documents Found"
              description={
                <span>
                  This agent has no knowledge sources. Please <b>upload documents in the Knowledge Studio first</b> to enable RAG for this agent.
                </span>
              }
              type="warning"
              showIcon
              icon={<InfoCircleOutlined />}
              style={{ marginBottom: 24 }}
            />
          ) : (
            <Form.Item
              label={<b>Link Knowledge Document</b>}
              name="linkedFileId"
              extra="Select a document from your Knowledge Library. This agent will use this file to answer questions using RAG."
            >
              <Select
                placeholder="Select a document from library"
                loading={libraryLoading}
                allowClear
                showSearch
                optionFilterProp="children"
                size="large"
              >
                {libraryFiles.map(file => (
                  <Select.Option key={file._id} value={file._id}>
                    <Space>
                      <BookOutlined style={{ color: '#1890ff' }} />
                      {file.originalName}
                      <Tag fontSize={10}>{file.templateId}</Tag>
                    </Space>
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}
          <Alert
            message="How it works"
            description="When a user asks a question, the agent will search this document for relevant information before generating a response."
            type="info"
            showIcon
            style={{ marginTop: 16 }}
          />
        </div>
      )
    },
    {
      key: 'functions',
      label: <span><ThunderboltOutlined /> Capabilities</span>,
      children: (
        <div style={{ padding: '16px 0' }}>
          <div style={{ 
            padding: 12, 
            background: '#f0f5ff', 
            borderRadius: 6, 
            marginBottom: 16,
            border: '1px solid #d6e4ff'
          }}>
            <strong>Selected: {selectedFunctions.length} functions</strong>
            {selectedFunctions.length > 0 && (
              <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                {selectedFunctions.join(', ')}
              </div>
            )}
          </div>

          {Object.entries(groupedFunctions).map(([category, funcs]) => (
            <div key={category} style={{ marginBottom: 24 }}>
              <Typography.Title level={5}>{category}</Typography.Title>
              <Row gutter={[12, 12]}>
                {funcs.map((func) => (
                  <Col key={func.name} xs={24} sm={12} md={8}>
                    <Card
                      size="small"
                      className={`function-card ${selectedFunctions.includes(func.name) ? 'selected' : ''}`}
                      onClick={() => toggleFunction(func.name)}
                      style={{ 
                        position: 'relative',
                        cursor: 'pointer',
                        borderColor: selectedFunctions.includes(func.name) ? '#1890ff' : '#f0f0f0',
                        background: selectedFunctions.includes(func.name) ? '#e6f7ff' : '#fff'
                      }}
                      hoverable
                    >
                      {selectedFunctions.includes(func.name) && (
                        <div style={{ position: 'absolute', top: 8, right: 8, color: '#1890ff' }}>
                          <CheckOutlined />
                        </div>
                      )}
                      <strong>{func.name}</strong>
                      <div style={{ fontSize: 11, color: '#666', marginTop: 4, height: 40, overflow: 'hidden' }}>
                        {func.description}
                      </div>
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>
          ))}
        </div>
      )
    },
    {
      key: 'advanced',
      label: <span><SafetyCertificateOutlined /> Advanced</span>,
      children: (
        <div style={{ padding: '16px 0' }}>
          <Form.Item
            label="Set as Account Default"
            name="isDefault"
            valuePropName="checked"
            extra="If enabled, this agent will handle all new incoming messages by default."
          >
            <Switch checkedChildren="YES" unCheckedChildren="NO" />
          </Form.Item>

          <Form.Item
            label="Enable Human Handoff"
            name="handoffEnabled"
            valuePropName="checked"
          >
            <Switch checkedChildren="ON" unCheckedChildren="OFF" />
          </Form.Item>

          <Form.Item
            label={<b>Handoff Webhook URL</b>}
            name="handoffWebhookUrl"
            extra="If this agent triggers a human handoff, this URL will be called."
          >
            <Input 
              placeholder="https://your-api.com/webhooks/handoff" 
              prefix={<GlobalOutlined />} 
            />
          </Form.Item>

          <Form.Item
            label={<b>Handoff Condition</b>}
            name="handoffCondition"
            extra="Instruct the AI exactly when it should stop and hand over to a human."
          >
            <TextArea 
              rows={3} 
              placeholder="e.g. When the user asks for a refund or a manager..."
            />
          </Form.Item>
        </div>
      )
    }
  ];

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={initialValues}
    >
      <Tabs defaultActiveKey="general" items={tabItems} />

      <Divider style={{ margin: '12px 0 24px 0' }} />

      <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
        <Button type="default" onClick={onCancel} style={{ marginRight: 8 }}>
          Cancel
        </Button>
        <Button type="primary" htmlType="submit" size="large">
          {initialValues ? 'Update Template' : 'Create Template'}
        </Button>
      </Form.Item>
    </Form>
  );
};

export default TemplateForm;


